/**
 * Swing JSContext.setEditable(panel): каскад на детей только если editable панели изменился.
 * Middle всегда пишет PANEL is_editable в jsResult.states, даже когда в Swing это no-op
 * (форма 2609: setEditable(panel, 4286, true) при default true).
 */

import { resolvePanelIdByJsName } from '@/common/utils/collectOnServerEventScripts'

/**
 * Костыль формы данных: `resolvePanelIdByJsName` читает только `pageData.form.panels`.
 * Сюда же приходит и корень формы `{ panels }` (тесты, pre-build layoutForm, `pageData.form`).
 * Без обёртки поиск панели вернёт null.
 *
 * TODO: научить `resolveFormRoot` в collectOnServerEventScripts принимать и `{ form }`, и голый
 *   корень с `panels` — тогда этот helper можно удалить.
 */
function toPageDataForPanelLookup(formOrPageData: unknown): unknown {
  if (!formOrPageData || typeof formOrPageData !== 'object') return undefined
  const rec = formOrPageData as Record<string, unknown>
  if (rec.form != null && typeof rec.form === 'object') return formOrPageData
  return { form: formOrPageData }
}

/**
 * Двухаргументный `en.setEditable(name)`: Swing `getElement(name)` по `VisualEntity.getName()`.
 * FormBuilder: пустой jsName → имя панели `"panel" + id`. Числовой id — web-совместимость
 * с прежним каскадом по `panelId`.
 */
export function resolvePanelIdFromSetEditableName(
  formOrPageData: unknown,
  name: string,
): string | null {
  const trimmed = String(name ?? '').trim()
  if (!trimmed) return null
  const pageData = toPageDataForPanelLookup(formOrPageData)
  const direct = resolvePanelIdByJsName(pageData, trimmed)
  if (direct != null) return String(direct)
  const swingDefault = /^panel(\d+)$/i.exec(trimmed)
  if (!swingDefault) return null
  const byId = resolvePanelIdByJsName(pageData, swingDefault[1])
  return byId != null ? String(byId) : null
}

export type SetEditableNameTarget =
  | { kind: 'panel'; panelId: string }
  | { kind: 'form'; formId: string }
  | { kind: 'element' }

/** Куда слать 2-arg `setEditable(name)`: панель / форма / элемент (поле, без дочерних). */
export function classifySetEditableName(
  name: string,
  input: {
    form?: unknown
    formId?: string | number | null
    /** Смонтированные panelId, если XML form ещё не в pageData. */
    livePanelIds?: ReadonlySet<string>
  },
): SetEditableNameTarget {
  const trimmed = String(name ?? '').trim()
  if (!trimmed) return { kind: 'element' }
  const panelId = resolvePanelIdFromSetEditableName(input.form, trimmed)
  if (panelId != null) return { kind: 'panel', panelId }
  // Один resolve: XML, затем livePanelIds. Не второй обход registry в GlobalProvider.
  if (input.livePanelIds?.has(trimmed)) return { kind: 'panel', panelId: trimmed }
  const formId = String(input.formId ?? '').trim()
  if (formId && formId !== '-1' && formId === trimmed) return { kind: 'form', formId }
  return { kind: 'element' }
}

export function isXmlReadOnlyFlag(value: unknown): boolean {
  if (value == null) return false
  const raw =
    typeof value === 'object' && value !== null && '_text' in value
      ? (value as { _text?: unknown })._text
      : value
  const normalized = String(raw ?? '').trim().toLowerCase()
  return normalized === '1' || normalized === 'true'
}

/** VisualEntity.editable = true; SwingPanel ставит false только при атрибуте isReadOnly=1. */
export function resolvePanelXmlDefaultEditable(
  form: unknown,
  panelId: string | number | null | undefined,
): boolean {
  const pid = String(panelId ?? '').trim()
  if (!pid) return true
  const panels = (form as { panels?: Record<string, unknown> } | null | undefined)?.panels
  const panel = panels?.[pid] as { _attributes?: { isReadOnly?: unknown }; isReadOnly?: unknown } | undefined
  if (!panel) return true
  return !isXmlReadOnlyFlag(panel._attributes?.isReadOnly ?? panel.isReadOnly)
}

/**
 * Как `if (tmp.isEditable() != editable)` в swingui JSContext.setEditable.
 * Неизвестное текущее = XML default панели (обычно true).
 */
export function shouldCascadePanelEditable(
  currentPanelEditable: boolean | undefined,
  nextEditable: boolean,
  xmlDefaultEditable = true,
): boolean {
  const current = currentPanelEditable !== undefined ? currentPanelEditable : xmlDefaultEditable
  return current !== nextEditable
}

/**
 * Remount / registerElement: ELEMENT-state важнее.
 * PANEL is_editable:true не накладывать на ребёнка — это no-op в Swing и ломает XML isReadOnly=1.
 * PANEL is_editable:false — панель реально закрыли, ребёнок должен закрыться.
 */
export function resolveChildEditableFromPanelState(
  panelEditable: boolean | undefined,
): boolean | undefined {
  if (panelEditable === false) return false
  return undefined
}
